import React, { useCallback, useState } from 'react';

import { Heading } from '~/components/core';
import Button from '~/components/core/Atomic/Buttons/Button';
import Grid from '~/components/core/Atomic/Grid/Grid';
import PlusIcon from '~/components/icons/PlusIcon';
import useDataFetcher from '~/components/useDataFetcher';
import type { SupportedEventsResponse, WorkflowRuleParams } from '~/server_shared/openapi/data-contracts';

import { useSysconfig } from '../../SystemConfigurationScreen';

import { WorkflowRulesApiService } from './api/workflowRulesApi';
import { WorkflowRulesFilters } from './components/WorkflowRulesFilters';
import { WorkflowRulesTable } from './components/WorkflowRulesTable';
import { useWorkflowRulesPagination } from './hooks/useWorkflowRulesPagination';
import AddOrEditWorkflowRuleDialog from './AddOrEditWorkflowRuleDialog';

// Extended interface for frontend-specific properties
interface WorkflowRuleModelWithSummary {
  id: string;
  display_name: string;
  is_enabled: boolean;
  is_all_lobs: boolean;
  lobs: string[];
  event_key: string;
  workflow_name: string;
  context: string;
  is_all_sub_orgs: boolean;
  sub_organization_ids: number[];
  [key: string]: unknown;
}

interface FilteredParams {
  subOrgIds: number[];
  lobs: string[];
  searchTerm: string;
}

const WorkflowRulesTab: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organization } = useSysconfig();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isRuleDialogOpen, setIsRuleDialogOpen] = useState(false);
  const [filteredParams, setFilteredParams] = useState<FilteredParams>({
    subOrgIds: [],
    lobs: [],
    searchTerm: '',
  });
  const [ruleToEdit, setRuleToEdit] = useState<WorkflowRuleModelWithSummary | null>(null);

  // Sort state management
  const [sortByColumn, setSortByColumn] = useState<{ id: string; order: 'asc' | 'desc' }>({
    id: 'display_name',
    order: 'asc',
  });

  const sortString = `${sortByColumn.order === 'asc' ? '+' : '-'}${sortByColumn.id}`;

  // Use the new pagination hook
  const {
    pagination,
    pageResults: rules,
    isLoading: isLoadingRules,
    isError: isErrorRules,
    reload: reloadRules,
  } = useWorkflowRulesPagination({
    organizationId: organization.id,
    initialRowsPerPage: 25,
    sortByColumn: sortString,
    // Pass filtering parameters to server-side
    subOrganizationIds: filteredParams.subOrgIds.length > 0 ? filteredParams.subOrgIds : null,
    lobs: filteredParams.lobs.length > 0 ? filteredParams.lobs : null,
    searchTerm: filteredParams.searchTerm || null,
  });

  const {
    isLoading: isLoadingEvents,
    isError: isErrorEvents,
    data: supportedEventsResponse,
  } = useDataFetcher<SupportedEventsResponse>(
    `/api/v1/automation/workflow_engine/rules/${organization.id}/supported_events`
  );

  const supportedEvents = supportedEventsResponse?.events;

  const isLoading = isLoadingRules || isLoadingEvents;
  const isError = isErrorRules || isErrorEvents;

  const handleSubOrgChanged = (subOrgIds: number[]) => {
    setFilteredParams((params) => ({ ...params, subOrgIds }));
  };

  const handleLobsChanged = (lobs: string[]) => {
    setFilteredParams((params) => ({ ...params, lobs }));
  };

  const handleSearchTermChanged = (searchTerm: string) => {
    setFilteredParams((params) => ({ ...params, searchTerm }));
  };

  const handleSubmitRule = async (values: WorkflowRuleParams): Promise<void> => {
    if (ruleToEdit) {
      await updateWorkflowRule(ruleToEdit.id, values);
    } else {
      await createWorkflowRule(values);
    }
  };

  const handleCloseRuleDialog = () => {
    setIsRuleDialogOpen(false);
    setRuleToEdit(null);
  };

  const handAddRuleClicked = () => {
    setIsRuleDialogOpen(true);
  };

  // Workflow Rules API Operations
  const createWorkflowRule = async (values: WorkflowRuleParams): Promise<void> => {
    setIsProcessing(true);
    try {
      await WorkflowRulesApiService.createWorkflowRule(organization.id, values);
      await reloadRules();
    } finally {
      setIsProcessing(false);
    }
  };

  const updateWorkflowRule = async (ruleId: string, values: WorkflowRuleParams): Promise<void> => {
    setIsProcessing(true);
    try {
      await WorkflowRulesApiService.updateWorkflowRule(organization.id, ruleId, values);
      await reloadRules();
    } finally {
      setIsProcessing(false);
    }
  };

  const deleteWorkflowRule = async (ruleId: string): Promise<void> => {
    setIsProcessing(true);
    try {
      await WorkflowRulesApiService.deleteWorkflowRule(organization.id, ruleId);
      await reloadRules();
    } finally {
      setIsProcessing(false);
    }
  };

  const setIsWorkflowRuleEnabled = async (ruleId: string, isEnabled: boolean): Promise<void> => {
    setIsProcessing(true);
    try {
      await WorkflowRulesApiService.setIsWorkflowRuleEnabled(organization.id, ruleId, isEnabled);
      await reloadRules();
    } finally {
      setIsProcessing(false);
    }
  };

  // Sort handler following established pattern
  const handleSort = useCallback(
    (columnId: string, order: 'asc' | 'desc') => {
      setSortByColumn({ id: columnId, order });
      pagination.setPage(0); // Reset to first page when sorting changes
    },
    [pagination]
  );

  // Adapter function to convert SortableTable callback signature
  const handleSortByColumn = useCallback(
    (_event: React.MouseEvent, { id, order }: { id: string; order: 'asc' | 'desc' }) => {
      handleSort(id, order);
    },
    [handleSort]
  );

  const handleDeleteRule = async (rule: WorkflowRuleModelWithSummary): Promise<void> => {
    await deleteWorkflowRule(rule.id);
  };

  const handEditRuleClicked = (rule: WorkflowRuleModelWithSummary) => {
    setRuleToEdit(rule);
    setIsRuleDialogOpen(true);
  };

  const handleEnabledChanged = async (rule: WorkflowRuleModelWithSummary): Promise<void> => {
    // Use dedicated enable endpoint for efficiency (consistent with other automation rules)
    await setIsWorkflowRuleEnabled(rule.id, !rule.is_enabled);
  };

  const onSubmit = async (values: WorkflowRuleParams): Promise<void> => {
    await handleSubmitRule(values);
    handleCloseRuleDialog();
  };

  return (
    <Grid container className="p-20">
      <Grid item xs={12} className="pb-12">
        <Heading variant={Heading.TYPES.H3}>Workflow Rules</Heading>
      </Grid>
      <Grid item xs={12} className="inline-flex justify-end">
        <Button
          color="primary"
          onClick={handAddRuleClicked}
          startIcon={<PlusIcon className="text-teal-700" size={14} iconColor="currentColor" />}
        >
          Add Rule
        </Button>
      </Grid>

      <WorkflowRulesFilters
        organization={organization}
        filteredParams={filteredParams}
        onSubOrgChanged={handleSubOrgChanged}
        onLobsChanged={handleLobsChanged}
        onSearchTermChanged={handleSearchTermChanged}
      />

      <Grid item xs={12}>
        <WorkflowRulesTable
          organization={organization}
          rules={rules as unknown as WorkflowRuleModelWithSummary[]}
          isLoading={isLoading}
          isError={isError}
          isProcessing={isProcessing}
          pagination={pagination}
          sortByColumn={sortByColumn}
          onSortByColumn={handleSortByColumn}
          onRefresh={async () => {
            await reloadRules();
          }}
          onEditRule={handEditRuleClicked}
          onDeleteRule={handleDeleteRule}
          onEnabledChanged={handleEnabledChanged}
        />
      </Grid>

      {isRuleDialogOpen ? (
        <AddOrEditWorkflowRuleDialog
          supportedEvents={supportedEvents}
          onCancel={handleCloseRuleDialog}
          onSubmit={onSubmit}
          rule={ruleToEdit ? (ruleToEdit as unknown as Record<string, unknown>) : undefined}
        />
      ) : null}
    </Grid>
  );
};

export default WorkflowRulesTab;
